import {
  createSection,
  featureBlockSchema,
  type Section,
} from '@/features/storefront/sections/schema';
import type { SectionPreset } from '@/previewThemes/types';

/**
 * Feast's own shelf of the "add section" library.
 *
 * This theme is a kitchen: pans, knives, the table they end up on. The shop's
 * generic sections can all express that, but only after a merchant has worked
 * out that "the dish of the week" is an image-with-text and that "what's in
 * the box" is a promises strip. These presets are that translation, made once
 * by whoever designed the theme - each one is an ordinary section, arranged
 * and worded the way this design intends it, and from the moment it lands on
 * the page it is edited, moved, hidden and rendered by exactly the same code
 * as everything else.
 *
 * Two rules they follow, both worth keeping if this file gets a sibling:
 *
 * - *no photography*. A preset ships copy and arrangement, never a picture
 *   from the theme's demo shoot. A merchant who publishes before noticing
 *   would be selling their pans under somebody else's photograph, and they
 *   would find out from a customer;
 * - *placeholder copy that is a label, never a claim*. "Tell them what makes
 *   it worth cooking with" is a prompt; "Handmade in Jaipur since 1974" is a
 *   sentence about a business this theme knows nothing about.
 */

/** The pages a merchandising band belongs on: anywhere a shopper is browsing. */
const BROWSING = ['home', 'listing', 'category', 'search'] as const;

function build(section: Section): Section {
  return section;
}

export const FEAST_PRESETS: readonly SectionPreset[] = [
  {
    id: 'dish-of-the-week',
    label: 'Dish of the week',
    description: 'One photograph beside the story of what you cooked in it.',
    type: 'image_with_text',
    build: () =>
      build({
        ...createSection('image_with_text'),
        heading: 'This week we are cooking',
        body: 'A short paragraph about the dish, and the pan it wants. Two or three sentences is plenty.',
        cta_label: 'Shop the pan',
        cta_href: '/products',
        image_side: 'left',
        padding: 'lg',
      }),
  },
  {
    id: 'recipe-note',
    label: 'Recipe note',
    description: 'The method, with the picture on the other side.',
    type: 'image_with_text',
    build: () =>
      build({
        ...createSection('image_with_text'),
        heading: 'How we make it',
        body: 'Write the method the way you would say it out loud. Nobody reads a recipe standing up.',
        image_side: 'right',
        padding: 'lg',
        background: 'surface',
      }),
  },
  {
    id: 'best-sellers',
    label: 'Best sellers',
    description: 'The eight things that leave the shelf fastest.',
    type: 'featured_products',
    pages: BROWSING,
    build: () =>
      build({
        ...createSection('featured_products'),
        title: 'Best *sellers*',
        subtitle: 'What our kitchens reach for first.',
        limit: 8,
        columns: 4,
      }),
  },
  {
    id: 'the-kitchen',
    label: 'The kitchen',
    description: 'Your departments, as the way in.',
    type: 'categories',
    pages: BROWSING,
    build: () =>
      build({
        ...createSection('categories'),
        title: 'Shop the *kitchen*',
        columns: 4,
      }),
  },
  {
    id: 'table-stories',
    label: 'Table stories',
    description: 'A wall of photographs, each one a link into the shop.',
    type: 'gallery',
    build: () =>
      build({
        ...createSection('gallery'),
        title: 'At the *table*',
        columns: 3,
        padding: 'lg',
      }),
  },
  {
    id: 'what-you-get',
    label: 'What is in the box',
    description: 'Delivery, returns and the guarantee, as a strip of promises.',
    type: 'features',
    build: () =>
      build({
        ...createSection('features'),
        title: '',
        columns: 4,
        background: 'surface',
        padding: 'md',
        blocks: [
          featureBlockSchema.parse({ icon: 'truck', title: 'Delivered in two days' }),
          featureBlockSchema.parse({ icon: 'shield', title: 'Ten-year guarantee' }),
          featureBlockSchema.parse({ icon: 'leaf', title: 'Made to be re-seasoned' }),
          featureBlockSchema.parse({ icon: 'support', title: 'Cook with us, any time' }),
        ],
      }),
  },
  {
    id: 'opening-hours',
    label: 'Opening hours',
    description: 'When the shop is open, in plain writing.',
    type: 'text',
    build: () =>
      build({
        ...createSection('text'),
        title: 'Come and see us',
        body: '<p>Monday to Friday, 9 to 6</p><p>Saturday, 10 to 4</p><p>Sunday, closed</p>',
        align: 'left',
        column_span: 'half',
      }),
  },
  {
    id: 'where-to-find-us',
    label: 'Where to find us',
    description: 'The address and the way in, beside the hours.',
    type: 'text',
    build: () =>
      build({
        ...createSection('text'),
        title: 'Where to find us',
        body: '<p>Your street address.</p><p>The nearest landmark, and where to park.</p>',
        align: 'left',
        column_span: 'half',
      }),
  },
  {
    id: 'what-cooks-say',
    label: 'What cooks say',
    description: 'Three quotes from people who own the pans.',
    type: 'testimonials',
    build: () =>
      build({
        ...createSection('testimonials'),
        title: 'What *cooks* say',
        columns: 3,
        padding: 'lg',
      }),
  },
  {
    id: 'kitchen-questions',
    label: 'Kitchen questions',
    description: 'Seasoning, induction, dishwashers - what shoppers ask before they buy.',
    type: 'faq',
    build: () =>
      build({
        ...createSection('faq'),
        title: 'Before you *cook*',
      }),
  },
  {
    id: 'promise-band',
    label: 'Promise band',
    description: 'One line across the page - free delivery, a deadline, a sale.',
    type: 'banner',
    build: () =>
      build({
        ...createSection('banner'),
        text: 'Free delivery on orders over ₹2,999',
        background: 'primary',
        padding: 'sm',
      }),
  },
];
